import { useState } from 'react';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { Copy, Link2 } from 'lucide-react';
import { toast } from 'sonner';
import { Button, ConfirmDialog, Input } from '@/shared/components';
import { formatDateTime } from '@/shared/lib/format';
import { toApiError, useApi } from '@/shared/api';

/**
 * „Link zum Weitergeben" — für den Fall, dass der Betreiber ihn selbst verschickt, etwa per
 * Messenger.
 *
 * Der bestehende Link wird hier bewusst nicht angezeigt — ihn öffnet nur „Onboarding-Link
 * öffnen“, auditiert. Es wird also ein **neuer** ausgestellt; die bisherigen werden dabei abgelöst
 * und gelten weiter — der
 * Gast behält, was in Mail und Chat steht. Die Rückfrage bleibt, weil der neue Link genau einmal
 * zu sehen ist.
 * Die URL steht danach genau einmal hier und wird bewusst nirgends zwischengespeichert; ein
 * Neuladen der Seite lässt sie verschwinden.
 */
export function ShareOnboardingLinkPanel({ bookingId }: { bookingId: string }) {
  const api = useApi();
  const qc = useQueryClient();
  const [confirm, setConfirm] = useState(false);
  const [issued, setIssued] = useState<{ url: string; expiresAt: string } | null>(null);

  const share = useMutation({
    mutationFn: () => api.system.onboardingShareLink(bookingId),
    onSuccess: async (result) => {
      setIssued(result);
      await qc.invalidateQueries({ queryKey: ['system', 'onboardingLinks'] });
    },
    onError: async (error) => toast.error((await toApiError(error)).detail ?? 'Fehler'),
  });

  const copy = async () => {
    if (!issued) return;
    try {
      await navigator.clipboard.writeText(issued.url);
      toast.success('Link kopiert');
    } catch {
      // Ohne sicheren Kontext oder Zwischenablage-Recht: der Link steht sichtbar im Feld und
      // lässt sich von Hand markieren. Eine stille Fehlmeldung wäre schlimmer als kein Kopieren.
      toast.error('Kopieren nicht möglich — Link bitte aus dem Feld übernehmen');
    }
  };

  return (
    <>
      <Button
        variant="ghost"
        size="sm"
        className="mt-2 w-full"
        leftIcon={<Link2 size={13} strokeWidth={2} />}
        onClick={() => setConfirm(true)}
        loading={share.isPending}
      >
        Link zum Weitergeben erzeugen
      </Button>

      {issued && (
        <div className="border-line/60 bg-card-alt/40 mt-3 rounded-md border p-3">
          <div className="flex items-center gap-2">
            <Input readOnly value={issued.url} onFocus={(e) => e.currentTarget.select()} />
            <Button
              variant="ghost"
              size="sm"
              leftIcon={<Copy size={13} strokeWidth={2} />}
              onClick={copy}
            >
              Kopieren
            </Button>
          </div>
          <p className="text-ink-soft mt-2 text-xs">
            Gültig bis {formatDateTime(issued.expiresAt)}. Wird <strong>nicht</strong> erneut
            angezeigt — beim Neuladen ist er weg, dann muss ein neuer erzeugt werden.
          </p>
        </div>
      )}

      <ConfirmDialog
        open={confirm}
        onOpenChange={setConfirm}
        title="Neuen Link erzeugen?"
        description="Der neue Link wird hier einmalig angezeigt und geht zusätzlich als Welcome-Mail an den Gast. Bisherige Links bleiben gültig."
        confirmLabel="Erzeugen"
        variant="brand"
        loading={share.isPending}
        onConfirm={async () => {
          await share.mutateAsync();
          setConfirm(false);
        }}
      />
    </>
  );
}
